import { memo } from "react";
import { toast } from "sonner";
import type { BotezBokehComponentProps } from "./types";
import { CORAL_TEXT_LARGE, FONT_BODY, FONT_DISPLAY, FONT_KICKER, FONT_SCRIPT, MUTED_TEXT, TEAL, TEAL_DARK, formatDate, getEventKind } from "./utils";
import { buildGoogleCalendarUrl, downloadIcsFile } from "@/utils/saveTheDate";

type BotezBokehSaveTheDateProps = BotezBokehComponentProps & {
  onShare?: () => Promise<void> | void;
};

export const BotezBokehSaveTheDate = memo(({ event, config, locale, t, onShare }: BotezBokehSaveTheDateProps) => {
  if (!event.date) return null;

  const { isBaptismOnly } = getEventKind(event);
  const descriptionKey = isBaptismOnly
    ? "events.form.template.saveTheDateDescriptionBaptism"
    : "events.form.template.saveTheDateDescription";

  // JUB-94: real calendar targets via the shared save-the-date helpers.
  const handleGoogleCalendar = () => {
    const url = buildGoogleCalendarUrl(event, locale);
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  const handleAppleCalendar = () => {
    downloadIcsFile(event, locale);
  };

  const handleShare = async () => {
    if (onShare) {
      await onShare();
      return;
    }
    if (navigator.share) {
      try {
        await navigator.share({ title: event.name, url: window.location.href });
      } catch {
        // User cancelled the share sheet — nothing to do.
      }
    } else {
      await navigator.clipboard.writeText(window.location.href);
      toast.success(t("common.copied"));
    }
  };

  const linkClassName =
    "inline-flex items-center justify-center min-h-11 px-4 w-full sm:w-auto rounded-full border text-xs uppercase tracking-[0.2em] transition-colors font-medium hover:opacity-70";
  const linkStyle = { fontFamily: FONT_KICKER, color: TEAL_DARK, borderColor: `${TEAL}4D` };

  return (
    <section
      id="save-the-date"
      className="relative z-10 py-16 border-t-2"
      style={{ background: config.palette.background, borderColor: `${TEAL}1A` }}
    >
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-12">
          <h2
            className="text-4xl sm:text-5xl mb-6"
            style={{ fontFamily: FONT_SCRIPT, color: CORAL_TEXT_LARGE }}
          >
            {t("events.form.template.saveTheDate")}
          </h2>
          <p
            className="text-xl sm:text-2xl mb-2 font-bold break-words"
            style={{ fontFamily: FONT_DISPLAY, color: config.palette.text }}
          >
            {formatDate(event.date, locale, {
              weekday: "long",
              day: "numeric",
              month: "long",
              year: "numeric",
            })}
          </p>
          <p
            className="text-sm tracking-wider break-words"
            style={{ fontFamily: FONT_BODY, color: MUTED_TEXT }}
          >
            {t(descriptionKey)}
          </p>
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <button
            type="button"
            onClick={handleGoogleCalendar}
            className={linkClassName}
            style={linkStyle}
            aria-label={t("events.form.template.addToGoogleCalendar")}
            title={t("events.form.template.addToGoogleCalendar")}
          >
            Google Calendar
          </button>

          <button
            type="button"
            onClick={handleAppleCalendar}
            className={linkClassName}
            style={linkStyle}
            aria-label={t("events.form.template.downloadCalendarFile")}
            title={t("events.form.template.downloadCalendarFile")}
          >
            Apple Calendar
          </button>

          <button type="button" onClick={handleShare} className={linkClassName} style={linkStyle}>
            {t("events.form.template.share")}
          </button>
        </div>
      </div>
    </section>
  );
});

BotezBokehSaveTheDate.displayName = "BotezBokehSaveTheDate";
